/**
 * @graphql cc
 */
import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';

import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';

import { isUnauthorizedError } from '@confluence/error-boundary';
import { markErrorAsHandled } from '@confluence/graphql';

import type { useIsEditorToolbarDockedAtTopEditorPreferencesQuery as useIsEditorToolbarDockedAtTopEditorPreferencesQuery$data } from './__types__/useIsEditorToolbarDockedAtTopEditorPreferencesQuery';

const useIsEditorToolbarDockedAtTopEditorPreferencesQuery = gql`
	query useIsEditorToolbarDockedAtTopEditorPreferencesQuery {
		userPreferences {
			confluenceEditorSettings {
				toolbarDockingInitialPosition
			}
		}
	}
`;

type Options = {
	defaultDockedAtTop?: boolean;
};

// returns true if the editor toolbar is docked at the top (when platform_editor_controls experiment is off, or when it's on and the userPreference is "top" )
// otherwise returns false
export const useIsEditorToolbarDockedAtTop = ({ defaultDockedAtTop = false }: Options = {}) => {
	const { data, loading, error } =
		useQuery<useIsEditorToolbarDockedAtTopEditorPreferencesQuery$data>(
			useIsEditorToolbarDockedAtTopEditorPreferencesQuery,
			{ fetchPolicy: 'cache-only' },
		);

	if (editorExperiment('platform_editor_controls', 'control')) {
		return true;
	}

	if (loading) {
		// while loading use the default docking preference
		return defaultDockedAtTop;
	}

	if (error) {
		if (isUnauthorizedError(error)) {
			markErrorAsHandled(error);
		}
		// couldn't query for user's docking preference, use the default docking preference
		return defaultDockedAtTop;
	}

	// get graphql user preference value
	const dockingPreference =
		data?.userPreferences?.confluenceEditorSettings?.toolbarDockingInitialPosition;

	if (dockingPreference === 'top') {
		return true;
	}

	if (dockingPreference === 'none') {
		return false;
	}

	return defaultDockedAtTop;
};
